import React, { useState, useEffect } from 'react';
import {
  Box,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Switch,
  FormControlLabel,
  Typography,
  Button,
  Autocomplete,
  TextField,
  IconButton,
  Chip
} from '@mui/material';
import { formatDistanceToNow, format } from 'date-fns';
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import StandardDialog from '../base/standardDialog';
import SessionInfoDisplay from './SessionInfoDisplay';
import { AutoSearchMode, AutoSearchState, StartAutoSearchRequest, VehicleAutoSearch } from '../../../models/vehicleAutoSearch';
import { startAutoSearch, endAutoSearch, getAutoSearchStatusColor, getAutoSearchShortStatusLabel } from '../../../services/autoSessionService';
import { apiGet, SubscriptionRequiredError } from '../../../services/apiClient';
import { useSubscriptionDialog } from '../../../context/subscriptionDialogContext';
import { useTranslation } from 'react-i18next';
import { useTheme } from '@mui/material/styles';
import { COUNTRY_NAMES, CountryCode } from '../../../models/country';
import { COUNTRY_TO_FLAG } from '../../../utils/languageUtils';
import { useTenant, useLocalStorage } from '../../../hooks';
import DateTimeInput from '../../inputs/DateTimeInput';
import { getVehicle } from '../../../services/vehicleService';
import { API_ENDPOINTS, buildApiUrl } from '../../../config/apiEndpoints';
import { Booking } from '../../../models/booking';

interface AutoSearchDialogProps {
  open: boolean;
  vehicleId: number;
  searchSession: VehicleAutoSearch | null;
  onClose: () => void;
  onSuccess: () => void;
}

const COUNTRY_OPTIONS = Object.entries(COUNTRY_NAMES).map(([code, name]) => ({
  code: code as CountryCode,
  name,
  flag: COUNTRY_TO_FLAG[code as CountryCode] || ''
}));

const AutoSearchDialog: React.FC<AutoSearchDialogProps> = ({
  open,
  vehicleId,
  searchSession,
  onClose,
  onSuccess,
}) => {
  const { t } = useTranslation();
  const theme = useTheme();
  const { showSubscriptionDialog } = useSubscriptionDialog();
  const { tenant } = useTenant();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Form state
  const [searchMode, setSearchMode] = useState<AutoSearchMode>(AutoSearchMode.ANY);
  const [afterBookingId, setAfterBookingId] = useState<string>('');
  const [shouldReturnToRegCountry, setShouldReturnToRegCountry] = useState(false);
  const [selectedCountries, setSelectedCountries] = useState<string[]>([]);
  const [fullTruckLoad, setFullTruckLoad] = useState(false);
  const [isDateFilterEnabled, setIsDateFilterEnabled] = useState(false);
  const [startLoadingTime, setStartLoadingTime] = useState<Date | null>(null);
  const [endLoadingTime, setEndLoadingTime] = useState<Date | null>(null);
  const [resetKey, setResetKey] = useState<number>(0);
  const [countdownMinutes, setCountdownMinutes] = useState<number>(0);
  const [savedParams, setSavedParams] = useLocalStorage<Partial<StartAutoSearchRequest>>('autosearch-params', {});
  
  // Track if user has made any changes to prevent override from polling
  const [hasUserModified, setHasUserModified] = useState(false);

  // Helper function to calculate default loading times
  const calculateDefaultLoadingTimes = async (): Promise<{ start: Date; end: Date }> => {
    try {
      // Fetch vehicle to get current_booking_id
      const vehicle = await getVehicle(vehicleId);
      const maxDaysAhead = tenant?.maxLoadingDaysAhead ?? 3;
      const now = new Date();
      let defaultStartLoadingTime = now;

      if (vehicle.current_booking_id) {
        // Fetch all bookings and find the current one
        const bookingsUrl = buildApiUrl(API_ENDPOINTS.bookings.list);
        const bookingsResponse = await apiGet(bookingsUrl, { limit: 1000 });
        const bookingsData = await bookingsResponse.json();
        const bookings: Booking[] = bookingsData.result || [];
        
        const currentBooking = bookings.find(b => b.id === vehicle.current_booking_id);
        
        if (currentBooking && currentBooking.endTime) {
          // Calculate loading start from booking: endTime - 24h
          const bookingEndTime = new Date(currentBooking.endTime);
          const loadingStartFromBooking = new Date(bookingEndTime.getTime() - 24 * 60 * 60 * 1000);
          // Use max(now, bookingEndTime - 24h)
          defaultStartLoadingTime = now > loadingStartFromBooking ? now : loadingStartFromBooking;
        }
      }

      const defaultEndLoadingTime = new Date(defaultStartLoadingTime.getTime() + maxDaysAhead * 24 * 60 * 60 * 1000);
      return { start: defaultStartLoadingTime, end: defaultEndLoadingTime };
    } catch (error) {
      console.error('Error calculating default loading times:', error);
      const now = new Date();
      const maxDaysAhead = tenant?.maxLoadingDaysAhead ?? 3;
      const defaultEndLoadingTime = new Date(now.getTime() + maxDaysAhead * 24 * 60 * 60 * 1000);
      return { start: now, end: defaultEndLoadingTime };
    }
  };

  // Initialize form with session data only when modal opens or when user hasn't modified anything
  useEffect(() => {
    const initializeForm = async () => {
      // Only update if modal just opened (reset hasUserModified) or user hasn't made changes
      if (!open || hasUserModified) return;

      // Calculate default loading times first (for use if saved params are stale)
      const { start: defaultStartLoadingTime, end: defaultEndLoadingTime } = await calculateDefaultLoadingTimes();

      if (searchSession && searchSession.autoSearchState === AutoSearchState.ON) {
        // Active session - use session parameters
        setSearchMode(searchSession.autoSearchMode);
        setAfterBookingId(searchSession.after_booking_id?.toString() || '');
        setShouldReturnToRegCountry(searchSession.shouldReturnRegCountry || false);
        setSelectedCountries(searchSession.selectedCountries || []);
        setFullTruckLoad(searchSession.fullTruckLoad || false);
        // Set loading times from session - respect the session's configuration
        // If the session has both times, enable the filter; otherwise disable it
        if (searchSession.startLoadingTime && searchSession.endLoadingTime) {
          // Replace stale (in the past) session times with safe defaults to avoid DATE_OUT_OF_RANGE on save.
          const sessionStartMs = new Date(searchSession.startLoadingTime).getTime();
          const sessionEndMs = new Date(searchSession.endLoadingTime).getTime();
          const nowMs = Date.now();
          setIsDateFilterEnabled(true);
          setStartLoadingTime(sessionStartMs >= nowMs ? new Date(sessionStartMs) : defaultStartLoadingTime);
          setEndLoadingTime(sessionEndMs >= nowMs ? new Date(sessionEndMs) : defaultEndLoadingTime);
        } else {
          // Session has no loading times configured - disable the filter
          setIsDateFilterEnabled(false);
          setStartLoadingTime(null);
          setEndLoadingTime(null);
        }
      } else {
        // No active session - try to restore from localStorage
        const params = savedParams as StartAutoSearchRequest;
        const hasValidStartTime = params.start_loading_time && new Date(params.start_loading_time).getTime() >= Date.now();
        const hasValidEndTime = params.end_loading_time && new Date(params.end_loading_time).getTime() >= Date.now();
        const isDateFilterEnabled = params.start_loading_time || params.end_loading_time;
        setSearchMode(params.auto_search_mode ?? AutoSearchMode.SELECTED_COUNTRIES);
        setShouldReturnToRegCountry(params.should_return_reg_country ?? false);
        setSelectedCountries(params.selected_countries ?? []);
        setFullTruckLoad(params.full_truck_load ?? true);
        setIsDateFilterEnabled(!!isDateFilterEnabled);
        setStartLoadingTime(isDateFilterEnabled && hasValidStartTime ? new Date(params.start_loading_time!) :  isDateFilterEnabled ? defaultStartLoadingTime : null);
        setEndLoadingTime(isDateFilterEnabled && hasValidEndTime ? new Date(params.end_loading_time!) : isDateFilterEnabled ? defaultEndLoadingTime : null);
      }
      setError(null);
    };

    initializeForm();
  }, [searchSession, open, hasUserModified, tenant?.maxLoadingDaysAhead, vehicleId]);

  useEffect(() => { // Reset the modification flag when modal opens

    if (open) {
      setHasUserModified(false);
    }
  }, [open]);

  // Check if session is currently ON
  const isSessionOn = searchSession?.autoSearchState === AutoSearchState.ON;

  // Update countdown timer
  useEffect(() => {
    if (!isSessionOn || !searchSession?.toCloseAt) return;

    const updateCountdown = () => {
      const now = new Date().getTime();
      const closeTime = new Date(searchSession.toCloseAt).getTime();
      const remainingMs = closeTime - now;
      const remainingMinutes = Math.max(0, Math.ceil(remainingMs / (1000 * 60)));
      setCountdownMinutes(remainingMinutes);
    };

    updateCountdown();
    const interval = setInterval(updateCountdown, 30000);
    return () => clearInterval(interval);
  }, [isSessionOn, searchSession?.toCloseAt]);

  const handleSave = async (): Promise<void> => {
    setLoading(true);
    setError(null);

    try {
      // Validate selected countries if mode is SELECTED_COUNTRIES
      if (searchMode === AutoSearchMode.SELECTED_COUNTRIES) {
        const validCountries = selectedCountries.filter(c => c && c.trim() !== '');
        if (validCountries.length === 0) {
          setError(t('Please select at least one country'));
          setLoading(false);
          return;
        }
      }

      // If session exists, end it first (without canceling negotiations)
      if (searchSession) {
        await endAutoSearch(vehicleId, {
          cancel_negotiations: false,
          send_email: false,
        });
      }

      // Start new session with updated parameters
      await startAutoSearch(vehicleId, {
        auto_search_mode: searchMode,
        after_booking_id: afterBookingId ? parseInt(afterBookingId) : undefined,
        should_return_reg_country: shouldReturnToRegCountry,
        selected_countries: searchMode === AutoSearchMode.SELECTED_COUNTRIES ? selectedCountries.filter(c => c && c.trim() !== '') : undefined,
        full_truck_load: fullTruckLoad,
        start_loading_time: startLoadingTime ? startLoadingTime.getTime() : undefined,
        end_loading_time: endLoadingTime ? endLoadingTime.getTime() : undefined,
      });

      // Save parameters to localStorage for future use
      const paramsToStore: StartAutoSearchRequest = {
        auto_search_mode: searchMode,
        after_booking_id: +afterBookingId || undefined,
        should_return_reg_country: searchMode === AutoSearchMode.PREFERRED_DESTINATIONS ? shouldReturnToRegCountry : undefined,
        selected_countries: searchMode === AutoSearchMode.SELECTED_COUNTRIES ? selectedCountries : undefined,
        full_truck_load: fullTruckLoad || undefined,
        start_loading_time: startLoadingTime?.getTime() || undefined,
        end_loading_time: endLoadingTime?.getTime() || undefined,
      };
      setSavedParams(paramsToStore);

      onSuccess();
      onClose();
    } catch (err: any) {
      // Handle subscription errors
      if (err instanceof SubscriptionRequiredError) {
        showSubscriptionDialog(err.subscriptionType);
        onClose();
        return;
      }

      const errorKey = err?.errorData?.error?.additionalInfo || err?.errorData?.error?.errorKey;
      switch (errorKey) {
        case 'client.extended.noAutoSearchSubscription':
          setError(t('No auto search subscription.'));
          break;
        case 'client.extended.autoSearchMaxDailyReached':
          setError(t('Auto search daily limit reached.'));
          break;
        case 'client.extended.autoSearchAlreadyStarted':
          setError(t('Auto search is already in progress.'));
          break;
        case 'client.body.auth':
          setError(t('Authentication failed.'));
          break;
        case 'internal.db':
          setError(t('A server error occurred. Please try again later.'));
          break;
        default:
          setError(t('Failed to start auto search session'));
      }
    } finally {
      setLoading(false);
    }
  };

  const handleStop = async (): Promise<void> => {
    setLoading(true);
    setError(null);

    try {
      await endAutoSearch(vehicleId, {
        cancel_negotiations: false,
        send_email: false,
      });

      onSuccess();
      onClose();
    } catch (err: any) {
      setError(err.message || t('Failed to stop auto search'));
    } finally {
      setLoading(false);
    }
  };

  const formatDateWithRelative = (date: Date | string | undefined): { relative: string; absolute: string } => {
    if (!date) return { relative: t('Never'), absolute: '' };
    const d = new Date(date);
    const relative = formatDistanceToNow(d, { addSuffix: true });
    const absolute = format(d, 'PPpp'); // e.g., "Jan 28, 2026, 2:30:45 PM"
    return { relative, absolute };
  };

  // Check if any values have changed from the original session values
  const hasChanges = (): boolean => {
    if (!isSessionOn) {
      // For new sessions, always allow starting
      return true;
    }
    // Compare current form values with session values
    const originalMode = searchSession?.autoSearchMode ?? AutoSearchMode.ANY;
    const originalAfterBookingId = searchSession?.after_booking_id?.toString() || '';
    const originalShouldReturn = searchSession?.shouldReturnRegCountry ?? false;
    const originalSelectedCountries = searchSession?.selectedCountries || [];
    const originalFullTruckLoad = searchSession?.fullTruckLoad ?? false;

    const originalStartLoadingTime = searchSession?.startLoadingTime ? new Date(searchSession.startLoadingTime).getTime() : null;
    const originalEndLoadingTime = searchSession?.endLoadingTime ? new Date(searchSession.endLoadingTime).getTime() : null;
    const currentStartLoadingTime = startLoadingTime ? startLoadingTime.getTime() : null;
    const currentEndLoadingTime = endLoadingTime ? endLoadingTime.getTime() : null;

    return (
      searchMode !== originalMode ||
      afterBookingId !== originalAfterBookingId ||
      shouldReturnToRegCountry !== originalShouldReturn ||
      fullTruckLoad !== originalFullTruckLoad ||
      currentStartLoadingTime !== originalStartLoadingTime ||
      currentEndLoadingTime !== originalEndLoadingTime ||
      JSON.stringify(selectedCountries.sort()) !== JSON.stringify(originalSelectedCountries.sort())
    );
  };

  return (
    <StandardDialog
      open={open}
      onClose={onClose}
      title={isSessionOn ? t('Edit Auto Search') : t('Start Auto Search')}
      subtitle={t('Configure automatic search parameters')}
      width={{ xs: '95%', sm: '85%', md: '550px', lg: '650px' }}
      autoSize={true}
      error={error}
      headerActions={
        isSessionOn ? (
          <Button
            variant="outlined"
            color="error"
            onClick={handleStop}
            disabled={loading}
            size="small"
          >
            {t('Stop Search')}
          </Button>
        ) : undefined
      }
      primaryAction={{
        label: isSessionOn ? t('Save Changes') : t('Start Search'),
        onClick: handleSave,
        loading: loading,
        disabled: loading || !hasChanges(),
      }}
      secondaryAction={{
        label: t('Cancel'),
        onClick: onClose,
        disabled: loading,
      }}
      contentSx={{ p: { xs: 2, sm: 2.5, md: 3 } }}
    >
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
        {/* Search Mode */}
        <FormControl fullWidth>
          <InputLabel>{t('Search Mode')}</InputLabel>
          <Select
            value={searchMode}
            label={t('Search Mode')}
            onChange={(e) => {
              setSearchMode(e.target.value as AutoSearchMode);
              setHasUserModified(true);
            }}
            disabled={loading}
          >
            <MenuItem value={AutoSearchMode.CIRCLE_TRIP}>
              {t('Circle Trip')}
            </MenuItem>
            <MenuItem value={AutoSearchMode.PREFERRED_DESTINATIONS}>
              {t('Preferred Destinations')}
            </MenuItem>
            <MenuItem value={AutoSearchMode.SELECTED_COUNTRIES}>
              {t('Selected Countries')}
            </MenuItem>
            <MenuItem value={AutoSearchMode.ANY}>
              {t('Any Country')}
            </MenuItem>
          </Select>
          { searchMode === AutoSearchMode.CIRCLE_TRIP &&
            <Box sx={{ mt: 1, color: 'text.secondary' }}>
              <Typography variant="body2">
                {t("Searches for offers along the vehicle's assigned route, supporting circular paths. Ideal for vehicles that follow a defined route with automatic return.")}
              </Typography>
            </Box>
          }
          { searchMode === AutoSearchMode.PREFERRED_DESTINATIONS &&
            <Box sx={{ mt: 1, color: 'text.secondary' }}>
              <Typography variant="body2">
                {t('Searches for offers going through your configured preferred destinations. . Uses priority scores to choose optimal destinations.')}
              </Typography>
            </Box>
          }
          { searchMode === AutoSearchMode.ANY &&
            <Box sx={{ mt: 1, color: 'text.secondary' }}>
              <Typography variant="body2">
                {t('Searches for offers to any destination without restrictions. Suitable for vehicles without specific route preferences or when maximum flexibility is desired.')}
              </Typography>
            </Box>
          }
          { searchMode === AutoSearchMode.SELECTED_COUNTRIES &&
            <Box sx={{ mt: 1, color: 'text.secondary' }}>
              <Typography variant="body2">
                {t('Searches for offers only in the countries you select. Choose specific countries where you want to operate.')}
              </Typography>
            </Box>
          }
        </FormControl>

        {/* Country Selection - Only for Selected Countries Mode */}
        {searchMode === AutoSearchMode.SELECTED_COUNTRIES && (
          <Box>
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
              <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                {t('Selected Countries')}
              </Typography>
              <Chip
                label={`${selectedCountries.length > 0 ? selectedCountries.length : 1} ${t('selected')}`}
                size="small"
                sx={{
                  color: 'text.white',
                  backgroundColor: 'text.purple',
                  fontWeight: 600,
                }}
              />
            </Box>

            {(selectedCountries.length > 0 ? selectedCountries : ['']).map((countryCode, index) => {
              const countryOption = COUNTRY_OPTIONS.find(c => c.code === countryCode);
              return (
                <Box key={index} sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 1.5 }}>
                  <Autocomplete
                    value={countryOption || null}
                    onChange={(_, newValue) => {
                      if (newValue) {
                        const newCountries = selectedCountries.length > 0 ? [...selectedCountries] : [];
                        newCountries[index] = newValue.code;
                        setSelectedCountries(newCountries);
                        setHasUserModified(true);
                      }
                    }}
                    options={COUNTRY_OPTIONS}
                    getOptionLabel={(option) => `${option.code} - ${option.name}`}
                    renderOption={(props, option) => (
                      <Box component="li" {...props} sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <span>{option.flag}</span>
                        <span>{option.code} - {option.name}</span>
                      </Box>
                    )}
                    slotProps={{
                      popper: {
                        placement: 'bottom-start',
                        modifiers: [
                          {
                            name: 'flip',
                            enabled: false,
                          },
                          {
                            name: 'preventOverflow',
                            enabled: true,
                          },
                        ],
                      },
                    }}
                    renderInput={(params) => {
                      const selectedOption = COUNTRY_OPTIONS.find(c => c.code === countryCode);
                      return (
                        <TextField
                          {...params}
                          label={t('Country')}
                          placeholder={t('Select country')}
                          InputProps={{
                            ...params.InputProps,
                            startAdornment: selectedOption?.flag ? (
                              <Box sx={{ ml: 1, mr: 0.5 }}>{selectedOption.flag}</Box>
                            ) : null,
                          }}
                        />
                      );
                    }}
                    fullWidth
                    disabled={loading}
                  />
                  {selectedCountries.length > 1 && (
                    <IconButton
                      onClick={() => {
                        const newCountries = selectedCountries.filter((_, i) => i !== index);
                        setSelectedCountries(newCountries);
                        setHasUserModified(true);
                      }}
                      disabled={loading}
                      size="small"
                      sx={{ color: 'text.red' }}
                    >
                      <DeleteIcon />
                    </IconButton>
                  )}
                </Box>
              );
            })}

            <Button
              startIcon={<AddIcon />}
              onClick={() => {
                setSelectedCountries([...selectedCountries, '']);
                setHasUserModified(true);
              }}
              disabled={loading}
              variant="outlined"
              size="small"
              sx={{ mt: 1 }}
            >
              {t('Add Country')}
            </Button>
          </Box>
        )}

        {/* After Booking ID TODO: implement with a list of bookings*/}
        {/*         <TextField
                  label={t('After Booking ID (Optional)')}
                  type="number"
                  value={afterBookingId}
                  onChange={(e) => setAfterBookingId(e.target.value)}
                  disabled={loading}
                  fullWidth
                  helperText={t('Search for offers suitable after this booking')}
                /> */}

        {/* Should Return to Reg Country - Only for Preferred Destinations */}
        {searchMode === AutoSearchMode.PREFERRED_DESTINATIONS && (
          <FormControlLabel
            control={
              <Switch
                checked={shouldReturnToRegCountry}
                onChange={(e) => {
                  setShouldReturnToRegCountry(e.target.checked);
                  setHasUserModified(true);
                }}
                disabled={loading}
              />
            }
            label={
              <Box>
                <Typography component="span">{t('Search offers back to registration country')}</Typography>
                <Typography variant="caption" color="text.secondary" display="block">
                  {t('(Requires return destinations to be configured in the Destinations tab of Settings.)')}
                </Typography>
              </Box>
            }
          />
        )}

        {/* Loading Time Window Filter */}
        <Box>
          <FormControlLabel
            control={
              <Switch
                checked={isDateFilterEnabled}
                onChange={async (e) => {
                  setIsDateFilterEnabled(e.target.checked);
                  setHasUserModified(true);
                  if (e.target.checked) {
                    // Calculate and set default loading times when user enables the filter
                    const { start, end } = await calculateDefaultLoadingTimes();
                    setStartLoadingTime(start);
                    setEndLoadingTime(end);
                  } else {
                    // Clear dates when disabled
                    setStartLoadingTime(null);
                    setEndLoadingTime(null);
                    setResetKey((prev) => prev + 1);
                  }
                }}
                disabled={loading}
              />
            }
            label={
              <Box>
                <Typography component="span">{t('Loading Time Window')}</Typography>
                <Typography variant="caption" color="text.secondary" display="block">
                  {t('Filter offers by their loading time window')}
                </Typography>
              </Box>
            }
          />
          
          {isDateFilterEnabled && (
            <Box sx={{ display: 'flex', flexDirection: 'row', gap: 2, mt: 2, pl: 4 }}>
              <DateTimeInput
                key={`start-time-${resetKey}`}
                label={t('Start Date')}
                type="date"
                size="small"
                fullWidth
                value={startLoadingTime}
                onChange={(value) => {
                  let date: Date | null = null;
                  if (value && typeof value === 'object' && 'toDate' in value) {
                    date = (value as any).toDate();
                  } else if (value) {
                    date = value as any as Date;
                  }
                  // Set time to 00:00:00
                  if (date) {
                    date.setHours(0, 0, 0, 0);
                  }
                  setStartLoadingTime(date);
                  setHasUserModified(true);
                }}
                InputLabelProps={{ shrink: true }}
                disabled={loading}
              />
              <DateTimeInput
                key={`end-time-${resetKey}`}
                label={t('End Date')}
                type="date"
                size="small"
                fullWidth
                value={endLoadingTime}
                onChange={(value) => {
                  let date: Date | null = null;
                  if (value && typeof value === 'object' && 'toDate' in value) {
                    date = (value as any).toDate();
                  } else if (value) {
                    date = value as any as Date;
                  }
                  // Set time to 23:59:59
                  if (date) {
                    date.setHours(23, 59, 59, 999);
                  }
                  setEndLoadingTime(date);
                  setHasUserModified(true);
                }}
                InputLabelProps={{ shrink: true }}
                disabled={loading}
              />
            </Box>
          )}
        </Box>

        {/* Full Truck Load Option */}
        <FormControlLabel
          control={
            <Switch
              checked={fullTruckLoad}
              onChange={(e) => {
                setFullTruckLoad(e.target.checked);
                setHasUserModified(true);
              }}
              disabled={loading}
            />
          }
          label={
            <Box>
              <Typography component="span">{t('Full Truck Load')}</Typography>
              <Typography variant="caption" color="text.secondary" display="block">
                {t('Only search for FTL offers (length ≥ {{minLength}}m)', { minLength: tenant?.minLengthMForFullTruckLoad ?? '?' })}
              </Typography>
            </Box>
          }
        />

        <Box sx={{ borderBottom: '1px solid', borderColor: 'divider' }} />

        {/* Display-only fields if session exists */}
        {searchSession && (
          <SessionInfoDisplay
            statusMessage={searchSession.statusMessage}
            statusColor={getAutoSearchStatusColor(theme, searchSession)}
            currentStatus={searchSession.autoSearchState}
            fields={[
              {
                label: t('Status: '),
                value: getAutoSearchShortStatusLabel(t, searchSession.autoSearchState),
                color: getAutoSearchStatusColor(theme, searchSession),
                bold: true,
              },
              {
                label: t('Started At'),
                value: formatDateWithRelative(searchSession.createdAt).absolute,
              },
              {
                label: t('Last Searched:'),
                value: searchSession.lastTimeSearchedAt
                  ? formatDateWithRelative(searchSession.lastTimeSearchedAt).relative
                  : t('Never'),
                tooltip: searchSession.lastTimeSearchedAt
                  ? formatDateWithRelative(searchSession.lastTimeSearchedAt).absolute
                  : undefined,
              },
              {
                label: t('Total Searches:'),
                value: searchSession.executions?.length || 0,
              },
              {
                label: t('Marketplace Offers:'),
                value: (searchSession.searches || []).reduce((sum, search) => sum + (search.history?.[0]?.marketplaceOffersFound ?? 0), 0),
              },
              ...(searchSession.executions && searchSession.executions.length > 1 ? [
                {
                  label: t('Marketplace Offers Found On Refresh:'),
                  value: (searchSession.searches || []).reduce((sum, search) => sum + (search.history?.length ? search.history[search.history.length - 1].marketplaceOffersFound : 0), 0),
                },
              ] : []),
              {
                label: t('Potential Offers Found:'),
                value: (searchSession.searches || []).reduce((sum, search) => sum + (search.history?.length ? search.history[search.history.length - 1].potentialOffersFound : 0), 0),
              },
              {
                label: t('Time Remaining:'),
                value: `${countdownMinutes} ${t('minutes')}`,
                color: theme.palette.text.purple,
                bold: true,
                showForStatus: AutoSearchState.ON,
              },
            ]}
          />
        )}
      </Box>
    </StandardDialog>
  );
};

export default AutoSearchDialog;

